import { useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { firebaseAuth } from '@/lib/firebase';
import { api, ApiError } from '@/lib/api';
import { useApiData } from '@/lib/useApi';
import { useAuth } from '@/auth/AuthContext';
import type { ConsultancyRow } from '@/lib/types';

const tabs = ['Consultancy Profile', 'My Profile', 'Notifications', 'Change Password', 'Team Roles'];

const ASSIGNMENT_MODES: { value: string; label: string; hint: string }[] = [
  { value: 'manual', label: 'Manual', hint: 'Counselors are chosen by hand when adding a student or lead.' },
  { value: 'round_robin', label: 'Auto — balanced', hint: 'New students/leads go to the active counselor with the fewest students.' },
  { value: 'by_destination', label: 'Auto — by destination', hint: 'Assigns to a counselor who handles that destination country (set per member in Team), else balanced.' },
];

const NOTIFICATION_OPTIONS: { key: string; label: string; desc: string }[] = [
  { key: 'newLead', label: 'New lead assigned', desc: 'When a lead is assigned to you.' },
  { key: 'appointmentReminder', label: 'Appointment reminders', desc: 'Upcoming appointments on your calendar.' },
  { key: 'applicationUpdate', label: 'Application updates', desc: 'Stage changes and offers on your applications.' },
  { key: 'paymentReceived', label: 'Payments received', desc: 'When a student payment is recorded.' },
  { key: 'taskAssigned', label: 'Tasks assigned', desc: 'When a task is assigned to you.' },
  { key: 'weeklySummary', label: 'Weekly summary', desc: 'A digest of your week every Monday.' },
];

const rolePermissions = [
  { role: 'Owner', canManageCounselors: true, canViewReports: true, canManageLeads: true, canApproveLeave: true },
  { role: 'Manager', canManageCounselors: true, canViewReports: true, canManageLeads: true, canApproveLeave: true },
  { role: 'Counselor', canManageCounselors: false, canViewReports: true, canManageLeads: true, canApproveLeave: false },
  { role: 'Visa Officer', canManageCounselors: false, canViewReports: true, canManageLeads: false, canApproveLeave: false },
  { role: 'Front Desk', canManageCounselors: false, canViewReports: false, canManageLeads: true, canApproveLeave: false },
  { role: 'Accountant', canManageCounselors: false, canViewReports: true, canManageLeads: false, canApproveLeave: false },
];

function ProfileTab() {
  const { data, loading } = useApiData<ConsultancyRow>(() => api.consultancy());
  const [form, setForm] = useState({ name: '', legalName: '', phone: '', city: '', country: '', assignmentMode: 'manual' });
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) setForm({ name: data.name || '', legalName: data.legalName || '', phone: data.phone || '', city: data.city || '', country: data.country || '', assignmentMode: data.assignmentMode || 'manual' });
  }, [data]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setMsg('');
    try { await api.updateConsultancy(form); setMsg('Saved.'); }
    catch (cause) { setMsg(cause instanceof ApiError ? cause.message : 'Could not save'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-8 text-sm text-gray-400">Loading…</div>;
  return (
    <form onSubmit={save} className="space-y-4 max-w-2xl">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Consultancy Name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-[#E1E5EA] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0B6E77]" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Legal Name</label><input value={form.legalName} onChange={(e) => setForm({ ...form, legalName: e.target.value })} className="w-full border border-[#E1E5EA] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0B6E77]" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Email</label><input value={data?.email || ''} disabled className="w-full border border-[#E1E5EA] rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Phone Number</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-[#E1E5EA] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0B6E77]" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">City</label><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full border border-[#E1E5EA] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0B6E77]" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Country</label><input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="w-full border border-[#E1E5EA] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0B6E77]" /></div>
      </div>
      <div className="pt-2 border-t border-[#E1E5EA]">
        <label className="block text-sm font-semibold text-[#3C3C3C] mb-1.5">Lead &amp; student assignment</label>
        <select value={form.assignmentMode} onChange={(e) => setForm({ ...form, assignmentMode: e.target.value })} className="w-full sm:w-80 border border-[#E1E5EA] rounded-lg px-3 py-2 text-sm bg-white outline-none focus:border-[#0B6E77]">{ASSIGNMENT_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}</select>
        <p className="text-xs text-gray-400 mt-1.5">{ASSIGNMENT_MODES.find((m) => m.value === form.assignmentMode)?.hint}</p>
      </div>
      {msg && <p className="text-sm text-gray-600">{msg}</p>}
      <button type="submit" disabled={saving} className="px-5 py-2.5 bg-[#0B6E77] text-white text-sm font-semibold rounded-lg hover:bg-[#085258] transition-colors cursor-pointer disabled:opacity-50">{saving ? 'Saving…' : 'Save Changes'}</button>
    </form>
  );
}

function MyProfileTab() {
  const { profile, refreshProfile } = useAuth();
  const [form, setForm] = useState({ fullName: '', phone: '' });
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (profile) setForm({ fullName: profile.fullName || '', phone: '' }); }, [profile]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setMsg('');
    try { await api.updateMe(form); await refreshProfile(); setMsg('Saved.'); }
    catch (cause) { setMsg(cause instanceof ApiError ? cause.message : 'Could not save'); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="space-y-4 max-w-md">
      <div><label className="block text-xs font-medium text-gray-600 mb-1">Full Name</label><input value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} className="w-full border border-[#E1E5EA] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0B6E77]" /></div>
      <div><label className="block text-xs font-medium text-gray-600 mb-1">Email</label><input value={profile?.email || ''} disabled className="w-full border border-[#E1E5EA] rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500" /></div>
      <div><label className="block text-xs font-medium text-gray-600 mb-1">Phone Number</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Update your phone" className="w-full border border-[#E1E5EA] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0B6E77]" /></div>
      {msg && <p className="text-sm text-gray-600">{msg}</p>}
      <button type="submit" disabled={saving} className="px-5 py-2.5 bg-[#0B6E77] text-white text-sm font-semibold rounded-lg hover:bg-[#085258] transition-colors cursor-pointer disabled:opacity-50">{saving ? 'Saving…' : 'Save Changes'}</button>
    </form>
  );
}

function NotificationsTab() {
  const { profile, refreshProfile } = useAuth();
  const [prefs, setPrefs] = useState<Record<string, boolean>>({});
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const existing = profile?.notificationPrefs || {};
    const initial: Record<string, boolean> = {};
    for (const opt of NOTIFICATION_OPTIONS) initial[opt.key] = existing[opt.key] ?? true;
    setPrefs(initial);
  }, [profile]);

  const save = async () => {
    setSaving(true); setMsg('');
    try { await api.updateMe({ notificationPrefs: prefs }); await refreshProfile(); setMsg('Saved.'); }
    catch (cause) { setMsg(cause instanceof ApiError ? cause.message : 'Could not save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3 max-w-2xl">
      <p className="text-sm text-gray-500">Choose which alerts you want to receive. These are saved to your profile.</p>
      {NOTIFICATION_OPTIONS.map((opt) => (
        <div key={opt.key} className="flex items-center justify-between border border-[#E1E5EA] rounded-xl p-4">
          <div><p className="text-sm font-medium text-[#3C3C3C]">{opt.label}</p><p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p></div>
          <button type="button" onClick={() => setPrefs({ ...prefs, [opt.key]: !prefs[opt.key] })} className={`w-11 h-6 rounded-full relative transition-colors cursor-pointer flex-shrink-0 ${prefs[opt.key] ? 'bg-green-500' : 'bg-gray-200'}`}><span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${prefs[opt.key] ? 'left-[22px]' : 'left-0.5'}`} /></button>
        </div>
      ))}
      {msg && <p className="text-sm text-gray-600">{msg}</p>}
      <button type="button" onClick={save} disabled={saving} className="px-5 py-2.5 bg-[#0B6E77] text-white text-sm font-semibold rounded-lg hover:bg-[#085258] transition-colors cursor-pointer disabled:opacity-50">{saving ? 'Saving…' : 'Save Preferences'}</button>
    </div>
  );
}

function PasswordTab() {
  const [form, setForm] = useState({ current: '', newPass: '', confirm: '' });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setMsg(null);
    if (form.newPass !== form.confirm) { setMsg({ ok: false, text: 'New passwords do not match.' }); return; }
    const user = firebaseAuth.currentUser;
    if (!user || !user.email) { setMsg({ ok: false, text: 'Not signed in.' }); return; }
    setSaving(true);
    try {
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, form.current));
      await updatePassword(user, form.newPass);
      setMsg({ ok: true, text: 'Password changed successfully.' });
      setForm({ current: '', newPass: '', confirm: '' });
    } catch (cause) {
      setMsg({ ok: false, text: cause instanceof Error ? cause.message : 'Could not change password' });
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
      {msg && <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${msg.ok ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}><i className={msg.ok ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} />{msg.text}</div>}
      {[['Current Password', 'current'], ['New Password', 'newPass'], ['Confirm New Password', 'confirm']].map(([label, key]) => (
        <div key={key}><label className="block text-xs font-medium text-gray-600 mb-1">{label}</label><input type="password" value={form[key as keyof typeof form]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="w-full border border-[#E1E5EA] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0B6E77]" required minLength={6} /></div>
      ))}
      <button type="submit" disabled={saving} className="px-5 py-2.5 bg-[#0B6E77] text-white text-sm font-semibold rounded-lg hover:bg-[#085258] transition-colors cursor-pointer disabled:opacity-50">{saving ? 'Updating…' : 'Update Password'}</button>
    </form>
  );
}

function RolesTab() {
  return (
    <div className="space-y-4 max-w-3xl">
      <p className="text-sm text-gray-500">Reference: what each role can access within the dashboard.</p>
      <div className="bg-white rounded-xl border border-[#E1E5EA] overflow-hidden"><div className="overflow-x-auto"><table className="w-full"><thead><tr className="border-b border-[#E1E5EA] bg-gray-50">{['Role', 'Manage Counselors', 'View Reports', 'Manage Leads', 'Approve Leave'].map((h) => <th key={h} className="text-left text-xs font-semibold text-gray-500 px-4 py-3 whitespace-nowrap">{h}</th>)}</tr></thead><tbody className="divide-y divide-gray-50">{rolePermissions.map((row) => <tr key={row.role} className="hover:bg-gray-50"><td className="px-4 py-3 text-sm font-semibold text-[#3C3C3C]">{row.role}</td>{[row.canManageCounselors, row.canViewReports, row.canManageLeads, row.canApproveLeave].map((val, i) => <td key={i} className="px-4 py-3"><div className={`w-5 h-5 flex items-center justify-center rounded-full ${val ? 'bg-green-100' : 'bg-gray-100'}`}><i className={`${val ? 'ri-check-line text-green-600' : 'ri-close-line text-gray-400'} text-xs`} /></div></td>)}</tr>)}</tbody></table></div></div>
    </div>
  );
}

const tabComponents: Record<string, React.FC> = {
  'Consultancy Profile': ProfileTab,
  'My Profile': MyProfileTab,
  'Notifications': NotificationsTab,
  'Change Password': PasswordTab,
  'Team Roles': RolesTab,
};

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('Consultancy Profile');
  const ActiveComp = tabComponents[activeTab];
  return (
    <DashboardLayout>
      <div className="space-y-4">
        <div className="flex items-center gap-1 bg-white border border-[#E1E5EA] rounded-xl p-1 flex-wrap">
          {tabs.map((tab) => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${activeTab === tab ? 'bg-[#0B6E77] text-white' : 'text-gray-500 hover:text-gray-700'}`}>{tab}</button>)}
        </div>
        <div className="bg-white rounded-xl border border-[#E1E5EA] p-6"><ActiveComp /></div>
      </div>
    </DashboardLayout>
  );
}
